import type { RGB } from 'pdf-lib'
import { unit, type MM } from './products-stickers.units'

/**
 * Avery templates for product stickers generation.
 * All coordinates and dimensions are defined in physical millimeters (MM)
 * matching standard printing sheets.
 */
export const AVERY_TEMPLATES = ['L4760-1x7', 'ZWECKFORM_6025-3x9', 'L7654-4x10'] as const
export type AveryTemplateName = (typeof AVERY_TEMPLATES)[number]

export type StickerArea =
  | {
      left: MM
      right?: never
      top: MM
      width: MM
      height: MM
    }
  | {
      left?: never
      right: MM
      top: MM
      width: MM
      height: MM
    }

export type StickerTextArea = StickerArea & {
  size: number
  maxLine: number // -1 for no crop
  bold: boolean
  color: RGB
  lineHeight?: number
}

export type AveryTemplate = {
  pageWidth: MM
  pageHeight: MM
  cols: number
  rows: number
  sheetInsetX: MM
  sheetInsetY: MM
  gutterX: MM
  gutterY: MM
  /** Commercial label dimensions in mm, used for UI display */
  displayDimensions: {
    width: MM
    height: MM
  }
  area: {
    webshopLogo: StickerArea
    productReference: StickerTextArea
    productBrand?: StickerTextArea
    productName: StickerTextArea
    productImage: StickerArea
    qrcode: StickerArea
    ean: StickerArea
  }
}

const PAGE_SIZE_A4 = {
  pageWidth: unit.mm(210),
  pageHeight: unit.mm(297),
}

const rgb = (red: number, green: number, blue: number): RGB =>
  ({ type: 'RGB', red, green, blue }) as unknown as RGB

const colorPrimary: RGB = rgb(63 / 255, 61 / 255, 58 / 255) // #3F3D3A
const colorSecondary: RGB = rgb(134 / 255, 130 / 255, 123 / 255) // #86827B
const colorCompact: RGB = rgb(77 / 255, 74 / 255, 70 / 255) // #4D4A46

/**
 * Derives the size of a single sticker from the sheet geometry,
 * assuming symmetric insets on both sides of the sheet.
 */
export function computeStickerSize(template: AveryTemplate): { width: MM; height: MM } {
  const { pageWidth, pageHeight, cols, rows, sheetInsetX, sheetInsetY, gutterX, gutterY } = template

  return {
    width: unit.mm((pageWidth - 2 * sheetInsetX - (cols - 1) * gutterX) / cols),
    height: unit.mm((pageHeight - 2 * sheetInsetY - (rows - 1) * gutterY) / rows),
  }
}

export const AVERY_TEMPLATES_MAP: Record<AveryTemplateName, AveryTemplate> = {
  'L4760-1x7': {
    ...PAGE_SIZE_A4,
    cols: 1,
    rows: 7,
    sheetInsetY: unit.mm(16),
    sheetInsetX: unit.mm(9),
    gutterX: unit.mm(0),
    gutterY: unit.mm(0),
    displayDimensions: {
      width: unit.mm(38),
      height: unit.mm(192),
    },
    area: {
      webshopLogo: {
        top: unit.mm(24.5),
        left: unit.mm(146.0),
        width: unit.mm(42.0),
        height: unit.mm(9.5),
      },
      productReference: {
        top: unit.mm(26.5),
        left: unit.mm(63.0),
        width: unit.mm(72.0),
        height: unit.mm(3.5),
        size: 9,
        bold: false,
        maxLine: 1,
        color: colorPrimary,
      },
      productBrand: {
        top: unit.mm(7.0),
        left: unit.mm(63.0),
        width: unit.mm(72.0),
        height: unit.mm(4.0),
        size: 10.5,
        bold: true,
        maxLine: 1,
        color: colorSecondary,
      },
      productName: {
        top: unit.mm(11.5),
        left: unit.mm(63.0),
        width: unit.mm(72.0),
        height: unit.mm(14.0),
        size: 10.5,
        lineHeight: 12,
        bold: true,
        maxLine: 3,
        color: colorPrimary,
      },
      productImage: {
        top: unit.mm(5.5),
        left: unit.mm(4.0),
        width: unit.mm(27.0),
        height: unit.mm(27.0),
      },
      qrcode: {
        top: unit.mm(7.0),
        left: unit.mm(34.0),
        width: unit.mm(24.0),
        height: unit.mm(24.0),
      },
      ean: {
        top: unit.mm(4.5),
        left: unit.mm(146.0),
        width: unit.mm(42.0),
        height: unit.mm(18.5),
      },
    },
  },
  'ZWECKFORM_6025-3x9': {
    ...PAGE_SIZE_A4,
    cols: 3,
    rows: 9,
    sheetInsetY: unit.mm(12),
    sheetInsetX: unit.mm(9),
    gutterX: unit.mm(6.1),
    gutterY: unit.mm(6.1),
    displayDimensions: {
      width: unit.mm(60),
      height: unit.mm(25),
    },
    area: {
      webshopLogo: {
        top: unit.mm(1.59),
        left: unit.mm(1.5),
        width: unit.mm(16.67),
        height: unit.mm(2.65),
      },
      productReference: {
        top: unit.mm(1.59),
        right: unit.mm(2.32),
        width: unit.mm(21.17),
        height: unit.mm(2.65),
        size: 7.5,
        bold: true,
        maxLine: 1,
        color: colorCompact,
      },
      productName: {
        top: unit.mm(17.2),
        left: unit.mm(2.12),
        width: unit.mm(55.56),
        height: unit.mm(5.5),
        size: 7.0,
        lineHeight: 8.5,
        bold: true,
        maxLine: 2,
        color: colorCompact,
      },
      productImage: {
        top: unit.mm(5.8),
        left: unit.mm(20.5),
        width: unit.mm(10.58),
        height: unit.mm(10.58),
      },
      qrcode: {
        top: unit.mm(5.8),
        left: unit.mm(2.12),
        width: unit.mm(10.58),
        height: unit.mm(10.58),
      },
      ean: {
        top: unit.mm(5.8),
        left: unit.mm(36.51),
        width: unit.mm(21.17),
        height: unit.mm(10.58),
      },
    },
  },
  'L7654-4x10': {
    ...PAGE_SIZE_A4,
    cols: 4,
    rows: 10,
    sheetInsetY: unit.mm(21.5),
    sheetInsetX: unit.mm(9.5),
    gutterX: unit.mm(2.75),
    gutterY: unit.mm(0),
    displayDimensions: {
      width: unit.mm(46),
      height: unit.mm(25),
    },
    area: {
      webshopLogo: {
        top: unit.mm(1.5),
        left: unit.mm(1.5),
        width: unit.mm(16.67),
        height: unit.mm(2.65),
      },
      productReference: {
        top: unit.mm(1.5),
        right: unit.mm(1.5),
        width: unit.mm(17.2),
        height: unit.mm(2.65),
        size: 7.5,
        bold: true,
        maxLine: 1,
        color: colorCompact,
      },
      productName: {
        top: unit.mm(17.2),
        left: unit.mm(1.5),
        width: unit.mm(42.875),
        height: unit.mm(6.5),
        size: 7.0,
        lineHeight: 8.5,
        bold: true,
        maxLine: 2,
        color: colorCompact,
      },
      productImage: {
        top: unit.mm(6.2),
        left: unit.mm(12.5),
        width: unit.mm(9.5),
        height: unit.mm(9.5),
      },
      qrcode: {
        top: unit.mm(6.2),
        left: unit.mm(1.5),
        width: unit.mm(9.5),
        height: unit.mm(9.5),
      },
      ean: {
        top: unit.mm(6.2),
        left: unit.mm(23.5),
        width: unit.mm(20.8),
        height: unit.mm(9.5),
      },
    },
  },
}
